import { useEffect, useMemo } from 'react';
import { AppointmentTypesTypes } from '@frontend/api-appointment-types';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { ManualTemplatesTypes, ManualTemplatesQueries } from '@frontend/api-manual-templates';
import { ReviewAppointmentLinkPatient } from '../../../../../../components/ReviewAppointmentLinkPatient';
import { ReviewAppointmentRequestFormV3 } from '../../../../../../components/ReviewAppointmentRequest/ReviewAppointmentRequestV3';
import { normalizeString } from '../../../../utils';
import { useScheduleRequestModalContext } from '../Context/ScheduleRequestModalContext';
import { useScheduleRequestModalFormContext } from '../Context/ScheduleRequestModalFormContext';
import { ReducedAppointmentType, ReducedPractitioner } from '../hooks/useReviewRequest';
import { SourceTenantListItem } from './types';

const { ManualTemplateTypes } = ManualTemplatesTypes;

type ScheduleRequestModalFormContainerV3Props = {
  appointmentWritebackSourceTenants: SourceTenantListItem[];
  patientWritebackSourceTenants: SourceTenantListItem[];
  isLoadingSourceTenants: boolean;
};

export const ScheduleRequestModalFormContainerV3 = ({
  appointmentWritebackSourceTenants,
  patientWritebackSourceTenants,
  isLoadingSourceTenants,
}: ScheduleRequestModalFormContainerV3Props) => {
  const { selectedScheduleRequest } = useScheduleRequestModalContext();

  const {
    personSelectorDialogProps,
    manageScheduleRequestFormDetails,
    manageScheduleRequestModalDetails,
    selectedPerson,
    personSelectorProps,
    setTemplate,
  } = useScheduleRequestModalFormContext();

  const {
    scheduleRequestApprovalForm: { values, seedValues, getFieldProps },
    customDateField,
    customStartTimeField,
    customEndTimeField,
  } = manageScheduleRequestFormDetails;

  const {
    access,
    isLoading: isLoadingModalDetails,
    isSyncAppDetailsLoading,
    providers: practitioners,
    workstations: operatories,
    appointmentTypes,
    tags,
  } = manageScheduleRequestModalDetails;

  const { data: scheduleRequestConfirmedTemplate } = ManualTemplatesQueries.useGetManualTemplateByType({
    templateType: ManualTemplateTypes.onlineSchedulingConfirmation,
    locationId: selectedScheduleRequest.locationId ?? '',
  });

  const { isIntegratedOffice, data: integrations } = DataSourcesQueries.useGetAccessibleIntegrations();

  /*
    Filtering of practitioners and operatories will need an update later. For now, showing all the practitioners and operatories for the location
    `practitioner.sourceId` and `operatory.sourceId` are the sourceTenantIds but named as sourceId during transformation to make it compatible with v2 data
  */
  const [filteredPractitionerList, filteredAppointmentTypes, filteredOperatoryList] = useMemo(() => {
    const filteredPractitioners = isIntegratedOffice
      ? practitioners?.filter((practitioner) => practitioner.sourceId)
      : practitioners.filter((practitioner) => {
          const isSelectedProvider =
            practitioner.id === selectedScheduleRequest.practitionerId ||
            normalizeString(practitioner.publicDisplayName) ===
              normalizeString(selectedScheduleRequest?.requestedOpenings?.[0]?.assets?.providers?.[0]);

          return isSelectedProvider || (!practitioner.hidden && practitioner.isActive);
        });

    const filteredAppointmentTypes: (AppointmentTypesTypes.AppointmentType | ReducedAppointmentType)[] =
      isIntegratedOffice
        ? appointmentTypes
        : appointmentTypes.filter((apptType) => {
            const isSelectedApptType =
              apptType.id === selectedScheduleRequest.appointmentTypeId ||
              normalizeString(apptType.name) === normalizeString(selectedScheduleRequest?.appointmentType);

            return isSelectedApptType || ('active' in apptType && apptType.active);
          });

    const filteredOperatories = isIntegratedOffice
      ? operatories?.filter((operatory) => operatory.workstationName && operatory.sourceId)
      : operatories;

    return [filteredPractitioners, filteredAppointmentTypes, filteredOperatories];
  }, [practitioners, selectedScheduleRequest, isIntegratedOffice, appointmentTypes, operatories]);

  // Update source form field value when there is only one source location
  useEffect(() => {
    let source = '';

    if (isIntegratedOffice && appointmentWritebackSourceTenants.length === 1) {
      source = appointmentWritebackSourceTenants[0].sourceTenantId || '';
    } else if (!isIntegratedOffice && integrations?.length === 1) {
      source = integrations?.[0]?.SourceID;
    }

    seedValues({
      ...values,
      source,
    });
  }, [isIntegratedOffice, appointmentWritebackSourceTenants.length, integrations?.length]);

  // Update workstation form field value when there is only one operatory
  useEffect(() => {
    if (filteredOperatoryList.length === 1) {
      seedValues({
        ...values,
        workstation: filteredOperatoryList[0]?.id,
      });
    }
  }, [filteredOperatoryList, isIntegratedOffice]);

  // Seed form values using selectedScheduleRequest
  useEffect(() => {
    let providerId = selectedScheduleRequest.practitionerId;
    let appointmentTypeId = selectedScheduleRequest.appointmentTypeId;

    // Find provider and appointment type IDs based on requested data for v2 appointment request when using combined requests
    if (selectedScheduleRequest.linkHash === 'v2') {
      if (!providerId) {
        providerId = filteredPractitionerList.find(
          (provider) =>
            normalizeString(provider.publicDisplayName) ===
            normalizeString(selectedScheduleRequest?.requestedOpenings?.[0]?.assets?.providers?.[0])
        )?.id;
      }
      if (!appointmentTypeId) {
        appointmentTypeId = appointmentTypes.find(
          (apptType) => normalizeString(apptType.name) === normalizeString(selectedScheduleRequest?.appointmentType)
        )?.id;
      }
    }

    if (selectedScheduleRequest) {
      seedValues({
        ...values,
        provider: providerId,
        appointmentType: appointmentTypeId,
      });
    }
  }, [selectedScheduleRequest, filteredPractitionerList.length, appointmentTypes.length]);

  const isLoading = isLoadingModalDetails || isSyncAppDetailsLoading || isLoadingSourceTenants;
  const showSourceLocationDropdown = isIntegratedOffice && appointmentWritebackSourceTenants.length > 1;
  const showWorkstationDropdown = filteredOperatoryList.length > 1;

  // Set configured template
  useEffect(() => {
    setTemplate(scheduleRequestConfirmedTemplate?.Template ?? '');
  }, [scheduleRequestConfirmedTemplate?.Template, setTemplate]);

  return (
    <>
      <ReviewAppointmentRequestFormV3
        appointmentTypes={filteredAppointmentTypes as AppointmentTypesTypes.AppointmentType[]}
        isLoading={isLoading}
        dateField={customDateField}
        startTimeField={customStartTimeField}
        endTimeField={customEndTimeField}
        getFieldProps={getFieldProps}
        providerList={filteredPractitionerList as ReducedPractitioner[]}
        selectedScheduleRequest={selectedScheduleRequest}
        showSourceLocationDropdown={showSourceLocationDropdown}
        showWorkstationDropdown={showWorkstationDropdown}
        appointmentWritebackSourceTenants={appointmentWritebackSourceTenants}
        values={values}
        workstationList={filteredOperatoryList}
        template={scheduleRequestConfirmedTemplate?.Template ?? ''}
        templateTags={tags}
        onTemplateChange={(template: string) => setTemplate(template)}
      />
      <ReviewAppointmentLinkPatient
        access={access}
        isIntegratedOffice={isIntegratedOffice}
        personSelectorProps={personSelectorProps}
        personSelectorDialogProps={personSelectorDialogProps}
        selectedPerson={selectedPerson}
        selectedScheduleRequest={selectedScheduleRequest}
        patientWritebackSourceTenants={patientWritebackSourceTenants}
      />
    </>
  );
};
